import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ShieldCheck, LogIn, UserPlus, ArrowRight, Building2, Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

/**
 * Public quote gate. Replaces the old direct link to `PublicQuoteRequestPage`
 * from the public Zion site. Visitors who click "Get a Quote" land here and
 * are funneled to:
 *   • Existing appointed producers → /login
 *   • New agencies seeking appointment → /register
 *   • Non-agents (insureds) → /ntoa (NTOA membership signup, then submit
 *     a quote from the member-portal Insurance tab)
 *
 * Keeps the public submission form hidden from search-engine bots and
 * info-seekers; only those who deliberately funnel through one of these
 * three paths can submit a quote.
 */
const GetAppointedGate = () => {
  const navigate = useNavigate();

  // SEO — explicitly noindex this entry funnel so crawlers don't index it
  // (the quote-submission form itself is no longer linked publicly).
  useEffect(() => {
    document.title = 'Get Appointed | Zion Underwriting Partners';
    const tag = document.createElement('meta');
    tag.name = 'robots';
    tag.content = 'noindex,nofollow';
    document.head.appendChild(tag);
    return () => { document.head.removeChild(tag); };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="get-appointed-gate">
      <Header />

      <main className="flex-1 pt-[140px] pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Hero */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1e3a5f]/5 border border-[#1e3a5f]/10 text-[#1e3a5f] text-xs font-semibold uppercase tracking-wider mb-4">
              <ShieldCheck className="h-3.5 w-3.5" /> Appointed Producers Only
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a5f] leading-tight">
              Submit a quote with Zion
            </h1>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Zion Underwriting Partners works exclusively through appointed retail
              producers. To submit a commercial trucking quote, sign in to your
              producer dashboard or get appointed below.
            </p>
          </div>

          {/* Two primary CTAs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
            {/* Existing producer */}
            <button
              onClick={() => navigate('/login')}
              className="group text-left bg-white border border-slate-200 hover:border-[#1e3a5f] hover:shadow-lg transition-all rounded-xl p-6"
              data-testid="gate-existing-producer-btn"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="h-11 w-11 rounded-lg bg-[#1e3a5f] text-white flex items-center justify-center">
                  <LogIn className="h-5 w-5" />
                </div>
                <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-[#1e3a5f] group-hover:translate-x-1 transition-all" />
              </div>
              <div className="text-base font-bold text-[#1e3a5f] mb-1">
                I'm an Appointed Producer
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Sign in to your producer dashboard to start a new quote, manage
                in-flight submissions, or check policy status.
              </p>
            </button>

            {/* New appointment */}
            <button
              onClick={() => navigate('/register')}
              className="group text-left bg-white border border-slate-200 hover:border-[#2c8a9b] hover:shadow-lg transition-all rounded-xl p-6"
              data-testid="gate-get-appointed-btn"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="h-11 w-11 rounded-lg bg-[#2c8a9b] text-white flex items-center justify-center">
                  <Building2 className="h-5 w-5" />
                </div>
                <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-[#2c8a9b] group-hover:translate-x-1 transition-all" />
              </div>
              <div className="text-base font-bold text-[#1e3a5f] mb-1">
                Get Appointed
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                New retail agency or managing general agency? Start the appointment
                process — once approved you'll have full access to bind authority
                and the producer dashboard.
              </p>
            </button>
          </div>

          {/* Divider */}
          <div className="relative my-10">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center"><span className="px-4 bg-slate-50 text-xs uppercase tracking-wider text-slate-400 font-semibold">or</span></div>
          </div>

          {/* Not-an-agent path → NTOA */}
          <div
            className="bg-gradient-to-br from-[#1e3a5f] to-[#2c8a9b] text-white rounded-xl p-7 sm:p-9 shadow-lg"
            data-testid="gate-ntoa-block"
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="h-10 w-10 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/70 font-semibold mb-1">
                  Not an Agent?
                </div>
                <h2 className="text-xl sm:text-2xl font-bold leading-snug">
                  Become an NTOA member — Zion will connect you with an agent.
                </h2>
              </div>
            </div>
            <p className="text-sm text-white/85 leading-relaxed mb-5 ml-14">
              The National Trucking Owners Association (NTOA) is open to every
              transportation operator — single-truck owner-operators, multi-truck
              fleets, lessors, and anything in between. Join, submit your
              insurance request from the <strong>Insurance</strong> tab of your
              member dashboard, and Zion will pair you with one of our appointed
              retail producers to walk you through the quote.
            </p>
            <div className="flex flex-wrap gap-3 ml-14">
              <Button
                onClick={() => navigate('/ntoa')}
                className="bg-white text-[#1e3a5f] hover:bg-white/90 font-semibold"
                data-testid="gate-ntoa-signup-btn"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Join NTOA &amp; Quote
              </Button>
              <Link
                to="/ntoa"
                className="text-white/80 hover:text-white text-xs underline self-center"
                data-testid="gate-ntoa-learn-link"
              >
                Learn more about NTOA membership →
              </Link>
            </div>
          </div>

          {/* Tiny footer note */}
          <p className="text-center text-xs text-slate-400 mt-8">
            Already an NTOA member?{' '}
            <Link to="/insured/login" className="underline hover:text-[#1e3a5f]" data-testid="gate-ntoa-member-login-link">
              Sign in to the member portal
            </Link>
            .
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default GetAppointedGate;
